import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import subscriptionService from './subscriptionService';

export interface PostLimitCheck {
  canPost: boolean;
  postsUsed: number;
  postsRemaining: number;
  limit: number;
  resetDate: Date;
  isUnlimited: boolean;
}

export interface MonthlyPostCount {
  userId: string;
  month: string; // YYYY-MM format
  count: number;
  lastReset: Date;
}

class PostLimitService {
  // Admin accounts with unlimited posting (bypass all limits)
  private readonly ADMIN_EMAILS = [
    'info@superkonnected.nl',
    'info@superkonnected.com',
    'bussinesssecurit@gmail.com'
  ];

  /**
   * Check if user is admin (unlimited posts)
   */
  private async isAdminUser(userId: string): Promise<boolean> {
    try {
      const { auth } = await import('../firebase');
      const user = auth.currentUser;
      if (user && this.ADMIN_EMAILS.includes(user.email?.toLowerCase() || '')) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get current month identifier (YYYY-MM)
   */
  private getCurrentMonth(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  /**
   * Get next month's first day for reset date
   */
  private getNextMonthStart(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }

  /**
   * Count social media posts for user in current month (excludes community feed posts)
   */
  async getMonthlyPostCount(userId: string): Promise<number> {
    try {
      const currentMonth = this.getCurrentMonth();
      const monthStart = new Date(currentMonth + '-01');
      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0, 23, 59, 59);

      // Count social shares (LinkedIn, Facebook, Twitter, etc.)
      const socialSharesQuery = query(
        collection(db, 'socialShares'),
        where('userId', '==', userId),
        where('timestamp', '>=', Timestamp.fromDate(monthStart)),
        where('timestamp', '<=', Timestamp.fromDate(monthEnd))
      );

      // Count scheduled posts that were published this month (social media only)
      const scheduledPostsQuery = query(
        collection(db, 'scheduledPosts'),
        where('userId', '==', userId),
        where('status', '==', 'published'),
        where('publishedAt', '>=', Timestamp.fromDate(monthStart)),
        where('publishedAt', '<=', Timestamp.fromDate(monthEnd))
      );

      const [socialSnapshot, scheduledSnapshot] = await Promise.all([
        getDocs(socialSharesQuery),
        getDocs(scheduledPostsQuery)
      ]);

      const totalSocialPosts = socialSnapshot.size + scheduledSnapshot.size;

      return totalSocialPosts;
    } catch (error) {
      console.error('Error counting monthly social media posts:', error);
      return 0;
    }
  }

  /**
   * Check if user can post based on their subscription limits
   */
  async checkPostLimit(userId: string): Promise<PostLimitCheck> {
    try {
      // ✅ Admin users have unlimited posts
      const isAdmin = await this.isAdminUser(userId);
      if (isAdmin) {
        return {
          canPost: true,
          postsUsed: 0,
          postsRemaining: -1,
          limit: -1,
          resetDate: this.getNextMonthStart(),
          isUnlimited: true
        };
      }

      // Get user subscription
      const subscription = await subscriptionService.getUserSubscription(userId);
      
      if (!subscription) {
        // Default to free tier if no subscription found
        return {
          canPost: false,
          postsUsed: 0,
          postsRemaining: 0,
          limit: 5,
          resetDate: this.getNextMonthStart(),
          isUnlimited: false
        };
      }

      // Check if user has unlimited posts (premium/enterprise)
      if (subscription.limitations.maxPosts === -1) {
        return {
          canPost: true,
          postsUsed: 0,
          postsRemaining: -1,
          limit: -1,
          resetDate: this.getNextMonthStart(),
          isUnlimited: true
        };
      }

      // Get current month's post count
      const postsUsed = await this.getMonthlyPostCount(userId);
      const limit = subscription.limitations.maxPosts;
      const postsRemaining = Math.max(0, limit - postsUsed);
      const canPost = postsUsed < limit;

      return {
        canPost,
        postsUsed,
        postsRemaining,
        limit,
        resetDate: this.getNextMonthStart(),
        isUnlimited: false
      };
    } catch (error) {
      console.error('Error checking post limit:', error);
      return {
        canPost: false,
        postsUsed: 0,
        postsRemaining: 0,
        limit: 5,
        resetDate: this.getNextMonthStart(),
        isUnlimited: false
      };
    }
  }

  /**
   * Get post limit information for display
   */
  async getPostLimitInfo(userId: string): Promise<{
    current: number;
    limit: number;
    remaining: number;
    resetDate: Date;
    isUnlimited: boolean;
    tier: string;
  }> {
    const limitCheck = await this.checkPostLimit(userId);
    const subscription = await subscriptionService.getUserSubscription(userId);
    
    return {
      current: limitCheck.postsUsed,
      limit: limitCheck.limit,
      remaining: limitCheck.postsRemaining,
      resetDate: limitCheck.resetDate,
      isUnlimited: limitCheck.isUnlimited,
      tier: subscription?.tier || 'free'
    };
  }

  /**
   * Check if user can post and throw error if not
   */
  async enforcePostLimit(userId: string): Promise<void> {
    const limitCheck = await this.checkPostLimit(userId);
    
    if (!limitCheck.canPost) {
      const subscription = await subscriptionService.getUserSubscription(userId);
      const tier = subscription?.tier || 'free';
      
      throw new Error(
        `Je hebt je maandelijkse post limiet bereikt (${limitCheck.postsUsed}/${limitCheck.limit}). ` +
        `Upgrade naar Premium voor onbeperkt posten, of wacht tot ${limitCheck.resetDate.toLocaleDateString('nl-NL')} voor een reset.`
      );
    }
  }
}

const postLimitService = new PostLimitService();
export default postLimitService;
